import React, { useState, useEffect } from 'react';
import { Download, Eye, ExternalLink, ChevronDown, CreditCard } from 'lucide-react';
import { $api, fetchClient } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import type { paths } from '../../api/paths';
import ErrorMessage from '../ErrorMessage';

type Invoice = paths['/api/organizations/{organization_id}/billing/invoices']['get']['responses']['200']['content']['application/json']['invoices'][0];

const Billing: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);

  // Fetch invoices with cursor pagination
  const invoicesQuery = $api.useQuery(
    'get',
    '/api/organizations/{organization_id}/billing/invoices',
    {
      params: {
        path: {
          organization_id: orgId,
        },
        query: {
          cursor,
          limit: '10',
        },
      },
    },
    {
      enabled: !!orgId,
    }
  );

  // Reset invoices when organization changes
  useEffect(() => {
    setAllInvoices([]);
    setCursor(undefined);
  }, [orgId]);

  // Accumulate invoices as we paginate
  useEffect(() => {
    if (invoicesQuery.data?.invoices) {
      if (cursor === undefined) {
        // First page - replace all invoices
        setAllInvoices(invoicesQuery.data.invoices);
      } else {
        // Subsequent pages - append to existing invoices
        setAllInvoices(prev => [...prev, ...invoicesQuery.data!.invoices]);
      }
    }
  }, [invoicesQuery.data?.invoices, cursor]);

  // Fetch Stripe portal URL when button is clicked
  const handleOpenPortal = async () => {
    if (portalUrl) {
      window.open(portalUrl, '_blank');
      return;
    }

    try {
      const response = await fetchClient.GET('/api/organizations/{organization_id}/billing/portal', {
        params: {
          path: {
            organization_id: orgId,
          },
          query: {
            return_url: window.location.href,
          },
        },
      });
      
      if (response.data?.url) {
        setPortalUrl(response.data.url);
        window.open(response.data.url, '_blank');
      } else if (response.error) {
        console.error('Failed to get portal URL:', response.error);
      }
    } catch (error) {
      console.error('Failed to get portal URL:', error);
    }
  };

  const hasMore = invoicesQuery.data?.has_more ?? false;
  const nextCursor = invoicesQuery.data?.next_cursor;
  const isLoadingMore = invoicesQuery.isLoading && cursor !== undefined;

  const handleLoadMore = () => {
    if (nextCursor && !isLoadingMore) {
      setCursor(nextCursor);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('paid') || statusLower === 'paid') return 'bg-green-100 text-green-800';
    if (statusLower.includes('pending') || statusLower === 'pending' || statusLower === 'open') return 'bg-yellow-100 text-yellow-800';
    if (statusLower.includes('overdue') || statusLower.includes('unpaid')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('paid') || statusLower === 'paid') return 'Payée';
    if (statusLower.includes('pending') || statusLower === 'pending' || statusLower === 'open') return 'En attente';
    if (statusLower.includes('overdue') || statusLower.includes('unpaid')) return 'En retard';
    return status;
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const isLoadingInvoices = invoicesQuery.isLoading && cursor === undefined;

  // Skeleton component
  const InvoiceTableSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <th key={i} className="px-6 py-3 text-left">
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[1, 2, 3].map((i) => (
            <tr key={i}>
              {[1, 2, 3, 4, 5, 6].map((j) => (
                <td key={j} className="px-6 py-4">
                  <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Facturation</h2>
          <p className="text-gray-600 mt-1">Gestion des factures et paiements</p>
        </div>
        <button 
          onClick={handleOpenPortal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <CreditCard className="h-4 w-4" />
          <span>Gérer les moyens de paiement</span>
        </button>
      </div>

      {/* Invoice Table */}
      {isLoadingInvoices ? (
        <InvoiceTableSkeleton />
      ) : invoicesQuery.error && allInvoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ErrorMessage error={invoicesQuery.error} />
        </div>
      ) : allInvoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">Aucune facture disponible</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Numéro de Facture
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date d'émission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Échéance
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{invoice.id}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                        {getStatusLabel(invoice.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.invoice_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.due_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {invoice.download_link && (
                        <>
                          <a 
                            href={invoice.download_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-900 transition-colors p-1 inline-block"
                            title="Voir la facture"
                          >
                            <Eye className="h-4 w-4" />
                          </a>
                          <a 
                            href={invoice.download_link} 
                            download
                            className="text-gray-600 hover:text-gray-900 transition-colors p-1 inline-block"
                            title="Télécharger la facture"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Chargement...</span>
                  </>
                ) : (
                  <>
                    <span>Charger plus</span>
                    <ChevronDown className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Billing;